import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Bell, Trash2, ArrowRight, MailCheck, Mail, Phone, Save } from "lucide-react";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import api, { errMessage } from "../lib/api";

// Renders an alert reason string. Server-generated reasons may contain
// <strong>…</strong> wrappers — convert them to React elements rather than
// rendering raw HTML (avoids XSS entirely).
function renderReason(text) {
  const parts = text.split(/<strong>(.*?)<\/strong>/g);
  return parts.map((part, idx) => {
    const k = `${idx}:${part.slice(0, 24)}`;
    return idx % 2 === 1 ? <strong key={k}>{part}</strong> : <span key={k}>{part}</span>;
  });
}

export default function Alerts() {
  const { user, refreshUser } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [phone, setPhone] = useState("");
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);

  useEffect(() => {
    if (user) {
      setPhone(user.phone || "");
      setSmsEnabled(user.sms_enabled !== false && !!user.phone);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable setState refs
  }, [user]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/alerts/history");
      setAlerts(r.data);
    } catch (e) {
      toast.error("Could not load alerts");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- module-level api singleton
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (id) => {
    await api.delete(`/alerts/${id}`);
    toast.success("Dismissed");
    load();
  };

  const savePrefs = async () => {
    setSavingPrefs(true);
    try {
      await api.patch("/auth/profile", {
        phone: phone.trim() || null,
        sms_enabled: !!(smsEnabled && phone.trim()),
      });
      await refreshUser();
      toast.success("SMS preferences saved");
    } catch (e) {
      toast.error(errMessage(e, "Could not save"));
    } finally {
      setSavingPrefs(false);
    }
  };

  return (
    <Layout>
      <div className="px-8 py-10 max-w-4xl">
        <div className="overline mb-3">// Inbox</div>
        <h1 className="font-display text-4xl font-bold tracking-tighter mb-2">
          Alerts.
        </h1>
        <p className="text-sm text-[--ink-soft] mb-6">
          Enable alerts on a saved search — we scan daily at 08:00 CT (13:00 UTC) and email + text you when anything meaningful changes.
        </p>

        <div data-testid="sms-prefs-card" className="surface-card p-5 mb-8">
          <div className="flex items-center gap-2 mb-3">
            <Phone size={14} strokeWidth={1.5} className="text-[--accent]" />
            <div className="overline">SMS preferences</div>
          </div>
          <div className="grid md:grid-cols-[1fr_auto_auto] gap-3 items-end">
            <label className="block">
              <span className="text-xs text-[--ink-muted] block mb-1">Mobile number</span>
              <input
                data-testid="sms-phone-input"
                type="tel"
                placeholder="+15551234567"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full"
              />
            </label>
            <label className="flex items-center gap-2 text-xs cursor-pointer select-none px-3 py-2 border border-[--line]">
              <input
                data-testid="sms-toggle"
                type="checkbox"
                checked={smsEnabled}
                onChange={(e) => setSmsEnabled(e.target.checked)}
                className="!p-0 !w-3.5 !h-3.5"
              />
              SMS on
            </label>
            <button
              data-testid="save-sms-prefs"
              onClick={savePrefs}
              disabled={savingPrefs}
              className="btn-primary flex items-center gap-1.5"
            >
              <Save size={12} strokeWidth={1.5} /> Save
            </button>
          </div>
          {!user?.phone && (
            <div className="text-xs text-[--ink-muted] mt-3 font-mono">
              No phone on file — alerts are email-only until you add one.
            </div>
          )}
        </div>

        {loading && (
          <div className="text-sm text-[--ink-muted]">Loading…</div>
        )}

        {!loading && alerts.length === 0 && (
          <div
            data-testid="alerts-empty"
            className="border border-dashed border-[--line] py-16 text-center"
          >
            <Bell size={24} strokeWidth={1.5} className="text-[--ink-muted] mx-auto mb-3" />
            <div className="text-sm text-[--ink-muted]">
              No alerts yet. Turn on alerts on a saved search from the{" "}
              <Link to="/saved" className="text-[--accent] underline underline-offset-2">
                Saved
              </Link>{" "}
              page.
            </div>
          </div>
        )}

        <div className="space-y-2" data-testid="alerts-list">
          {alerts.map((a) => (
            <div key={a.id} className="surface-card p-4">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="w-1 self-stretch bg-[--accent]" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="font-display font-bold text-base truncate">{a.summary}</div>
                      {a.email_sent ? (
                        <MailCheck size={12} className="text-[--success]" strokeWidth={1.5} />
                      ) : (
                        <Mail size={12} className="text-[--ink-muted]" strokeWidth={1.5} />
                      )}
                      {a.sms_sent && (
                        <Phone size={12} className="text-[--success]" strokeWidth={1.5} />
                      )}
                    </div>
                    <div className="text-xs text-[--ink-muted] font-mono mb-2">
                      {a.city}, {a.state} · {new Date(a.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <Link
                    to={`/market?city=${encodeURIComponent(a.city)}&county=${encodeURIComponent(a.county || "")}&state=${a.state}`}
                    data-testid={`open-alert-${a.id}`}
                    className="btn-primary !py-1.5 !px-3 text-xs flex items-center gap-1"
                  >
                    Open <ArrowRight size={12} strokeWidth={1.5} />
                  </Link>
                  <button
                    data-testid={`dismiss-alert-${a.id}`}
                    onClick={() => remove(a.id)}
                    className="btn-ghost !py-1.5 !px-3 text-xs"
                  >
                    <Trash2 size={12} strokeWidth={1.5} />
                  </button>
                </div>
              </div>
              <ul className="text-xs text-[--ink-soft] leading-relaxed pl-4 space-y-1 list-disc">
                {(a.reasons || []).map((r) => (
                  <li key={`${a.id}-${r.slice(0, 40)}`}>{renderReason(r)}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
